import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, MoreHorizontal, Play, Settings, Smartphone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { KioskSettings, KioskMode } from '../types';

const kioskModeLabels: Record<KioskMode, string> = {
  rotation: 'Rotation',
  search: 'Search',
  browse: 'Browse'
};

const kioskModeColors: Record<KioskMode, string> = {
  rotation: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  search: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  browse: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
};

export function KioskSettingsTable({ data }: { data: KioskSettings[] }) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns: ColumnDef<KioskSettings>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: 'Kiosk Settings',
      cell: ({ row }) => {
        const settings = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
              <Settings className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{settings.name}</span>
              <span className="text-muted-foreground text-sm truncate max-w-[300px]">
                {settings.description || 'No description'}
              </span>
            </div>
          </div>
        );
      },
      size: 300,
    },
    {
      accessorKey: 'defaultMode',
      header: 'Default Mode',
      cell: ({ row }) => {
        const mode = row.getValue('defaultMode') as KioskMode;
        return (
          <Badge className={kioskModeColors[mode]} variant="outline">
            {kioskModeLabels[mode]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: ({ row }) => {
        const isActive = row.getValue('active') as boolean;
        return (
          <Badge 
            className={isActive 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
            } 
            variant="outline"
          >
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'lastUpdated',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Last Updated
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const timestamp = row.getValue('lastUpdated') as number;
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to JS Date
        return (
          <div className="text-sm">
            {formatDistanceToNow(date, { addSuffix: true })}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const settings = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/kiosk/settings/$id" params={{ id: settings.id }}>View details</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/kiosk/settings/edit/$id" params={{ id: settings.id }}>Edit settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/kiosk/preview/$id" params={{ id: settings.id }}>
                  <Play className="mr-2 h-4 w-4" />
                  Preview kiosk
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/kiosk/devices" search={{ settingsId: settings.id }}>
                  <Smartphone className="mr-2 h-4 w-4" />
                  View devices
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                {settings.active ? 'Deactivate' : 'Activate'}
              </DropdownMenuItem>
              <DropdownMenuItem>Duplicate settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive">Delete settings</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter settings..."
            value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('name')?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Mode <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(kioskModeLabels).map(([mode, label]) => (
                <DropdownMenuCheckboxItem
                  key={mode}
                  checked={table
                    .getColumn('defaultMode')
                    ?.getFilterValue() as string[] | undefined
                    ? (table.getColumn('defaultMode')?.getFilterValue() as string[]).includes(mode)
                    : true
                  }
                  onCheckedChange={(value) => {
                    const filterValues = (table.getColumn('defaultMode')?.getFilterValue() as string[]) || [];
                    if (value) {
                      table.getColumn('defaultMode')?.setFilterValue([...filterValues, mode]);
                    } else {
                      table.getColumn('defaultMode')?.setFilterValue(filterValues.filter((val) => val !== mode));
                    }
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Status <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                checked={table
                  .getColumn('active')
                  ?.getFilterValue() as string[] | undefined
                  ? (table.getColumn('active')?.getFilterValue() as string[]).includes('true')
                  : true
                }
                onCheckedChange={(value) => {
                  const filterValues = (table.getColumn('active')?.getFilterValue() as string[]) || [];
                  if (value) {
                    table.getColumn('active')?.setFilterValue([...filterValues, 'true']);
                  } else {
                    table.getColumn('active')?.setFilterValue(filterValues.filter((val) => val !== 'true'));
                  }
                }}
              >
                Active
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={table
                  .getColumn('active')
                  ?.getFilterValue() as string[] | undefined
                  ? (table.getColumn('active')?.getFilterValue() as string[]).includes('false')
                  : true
                }
                onCheckedChange={(value) => {
                  const filterValues = (table.getColumn('active')?.getFilterValue() as string[]) || [];
                  if (value) {
                    table.getColumn('active')?.setFilterValue([...filterValues, 'false']);
                  } else {
                    table.getColumn('active')?.setFilterValue(filterValues.filter((val) => val !== 'false'));
                  }
                }}
              >
                Inactive
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm">
            <Link to="/kiosk/settings/add">
              <Settings className="mr-2 h-4 w-4" />
              Add Settings
            </Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No kiosk settings found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
